<script setup>
import { onBeforeMount, onBeforeUnmount, ref, watch } from "vue"
import { useHttpErrorsHandler } from "../../Composables/httpErrorsHandler"
import { useLocalStorage } from "../../Composables/useLocalStorage.js"
import { useDataManager } from "../../Composables/useDataManager.js"
import { usePlans } from '../../Composables/usePlans.js'
import DataTable from "../../Components/DataTable.vue"
import Modal from "../../Components/Modal.vue"

const { httpErrorsHandler } = useHttpErrorsHandler()

const storageKey = "Shiftech_PlanList"
const { saveToLocalStorage, loadFromLocalStorage } = useLocalStorage(storageKey)

const {
    url,
    data: plans,
    links,
    meta,
    loading,
    sortingField,
    sortingFieldsOrder,
    changeSorting,
    loadData,
} = useDataManager({
    baseUrl: '/api/admin/plans',
    defaultSortingField: 'sort_order',
    defaultSortingFieldsOrder: {
        id: 'asc',
        name: 'asc',
        sort_order: 'asc',
        created_at: 'asc',
    },
    onError: (error) => httpErrorsHandler(error),
})

const columns = ref([
    { label: '#ID', field: 'id', sortable: true, slot: 'id', class: 'col-1' },
    { label: 'Nom', field: 'name', sortable: true, slot: 'name', class: 'col-2' },
    { label: 'Prix', slot: 'price', class: 'col-1' },
    { label: 'Intervalle', slot: 'interval', class: 'col-1' },
    { label: 'Modules', slot: 'modules', class: 'col-1 text-center' },
    { label: 'Abonnés', slot: 'subscribers', class: 'col-1 text-center' },
    { label: 'Statut', slot: 'status', class: 'col-1 text-center' },
    { label: 'Stripe', slot: 'stripe', class: 'col-1 text-center' },
    { label: 'Actions', slot: 'actions', class: 'col-1 text-center' },
])

onBeforeMount(() => {
    const savedSettings = loadFromLocalStorage()
    if (savedSettings) {
        sortingField.value = savedSettings.sortingField || sortingField.value
        Object.assign(sortingFieldsOrder, savedSettings.sortingFieldsOrder || sortingFieldsOrder)
    }

    loadData()
})

watch(
    [sortingField, sortingFieldsOrder],
    ([newSortingField, newSortingOrder]) => {
        saveToLocalStorage({
            sortingField: newSortingField,
            sortingFieldsOrder: newSortingOrder,
        })
    },
    { deep: true }
)

onBeforeUnmount(() => {
    document.querySelectorAll('.modal').forEach(modalElement => {
        const modalInstance = bootstrap.Modal.getInstance(modalElement)
        if (modalInstance) {
            modalInstance.hide()
        }
    })
})

const planToDelete = ref(null)

function showModal(modalId, plan) {
    planToDelete.value = plan
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.show()
}

function hideModal(modalId) {
    const modale = bootstrap.Modal.getOrCreateInstance(document.getElementById(modalId))
    modale.hide()
}

const {
    deletePlan,
    syncToStripe,
} = usePlans({
    onDeleted: () => {
        loadData(url.value)
        hideModal('modalDelete')
    },
    onError: (error) => httpErrorsHandler(error),
})

const syncing = ref(false)
const handleSyncToStripe = async (planId) => {
    syncing.value = true
    await syncToStripe(planId)
    await loadData(url.value)
    syncing.value = false
}
</script>

<template>
    <div class="row">
        <div class="col-lg-12">
            <div class="card">
                <div class="card-header">
                    <div class="d-flex align-items-center">
                        <h5 class="card-title mb-0 flex-grow-1">Abonnements</h5>
                        <router-link :to="{ name: 'admin.plans.create' }" class="btn btn-secondary">
                            <i class="ri-add-fill me-1 align-bottom"></i> Ajouter
                        </router-link>
                    </div>
                </div>

                <div class="card-body">
                    <data-table
                        :columns="columns"
                        :data="plans"
                        :loading="loading"
                        :links="links"
                        :meta="meta"
                        :sorting-field="sortingField"
                        :sorting-order="sortingFieldsOrder"
                        @sort-changed="changeSorting"
                        @page-changed="loadData"
                    >
                        <!-- ID -->
                        <template #id="{ item }">
                            <span class="fw-medium text-dark">#{{ item.data.id }}</span>
                        </template>

                        <!-- Name -->
                        <template #name="{ item }">
                            <strong>{{ item.data.name }}</strong>
                        </template>

                        <!-- Price -->
                        <template #price="{ item }">
                            {{ item.data.price_formatted }}
                        </template>

                        <!-- Interval -->
                        <template #interval="{ item }">
                            {{ item.data.billing_interval_label }}
                        </template>

                        <!-- Modules -->
                        <template #modules="{ item }">
                            <span class="badge fs-11 rounded-pill bg-info-subtle text-info">
                                {{ item.data.modules?.length || 0 }}
                            </span>
                        </template>

                        <!-- Subscribers -->
                        <template #subscribers="{ item }">
                            <span
                                class="badge fs-11 rounded-pill"
                                :class="item.data.active_subscribers_count > 0 ? 'bg-primary-subtle text-primary' : 'bg-secondary-subtle text-secondary'"
                            >
                                {{ item.data.active_subscribers_count }}
                            </span>
                        </template>

                        <!-- Status -->
                        <template #status="{ item }">
                            <span class="badge fs-11 rounded-pill" :class="item.data.is_active ? 'bg-success-subtle text-success' : 'bg-secondary-subtle text-secondary'">
                                {{ item.data.is_active ? 'Actif' : 'Inactif' }}
                            </span>
                        </template>

                        <!-- Stripe -->
                        <template #stripe="{ item }">
                            <span v-if="item.data.stripe_product_id" class="badge fs-11 bg-success-subtle text-success">
                                <i class="ri-check-line align-middle"></i> Synchronisé
                            </span>
                            <a
                                v-else
                                href="#"
                                class="badge fs-11 bg-warning-subtle text-warning"
                                @click.prevent="handleSyncToStripe(item.data.id)"
                                :class="{ 'disabled': syncing }"
                            >
                                <span v-if="syncing" class="spinner-border spinner-border-sm me-1"></span>
                                <i v-else class="ri-refresh-line align-middle"></i> Synchroniser
                            </a>
                        </template>

                        <!-- Actions -->
                        <template #actions="{ item }">
                            <!-- UPDATE -->
                            <router-link
                                :to="{ name: 'admin.plans.edit', params: { id: item.data.id } }"
                                class="text-info"
                                title="Modifier le plan"
                                v-if="item.can.update"
                            >
                                <i class="ri-pencil-fill fs-17 px-1"></i>
                            </router-link>
                            <i class="ri-pencil-fill fs-17 px-1 text-muted" v-else></i>

                            <!-- DELETE -->
                            <a
                                href="#"
                                class="text-danger"
                                title="Supprimer le plan"
                                @click.prevent="showModal('modalDelete', item.data)"
                                v-if="item.can.delete"
                            >
                                <i class="ri-delete-bin-3-fill fs-17 px-1"></i>
                            </a>
                            <i
                                class="ri-delete-bin-3-fill fs-17 px-1 text-muted"
                                :title="item.data.active_subscribers_count > 0 ? `Impossible de supprimer : ${item.data.active_subscribers_count} abonné(s) actif(s)` : 'Suppression non autorisée'"
                                v-else
                            ></i>
                        </template>
                    </data-table>

                    <modal
                        id="modalDelete"
                        title="Supprimer le plan ?"
                        btn-class="btn-danger"
                        btn-text="Supprimer"
                        @click:button="deletePlan(planToDelete?.id)"
                    >
                        <template #icon>
                            <lord-icon
                                src="https://cdn.lordicon.com/gsqxdxog.json"
                                trigger="loop"
                                colors="primary:#212529,secondary:#f06548"
                                style="width:90px;height:90px"
                            ></lord-icon>
                        </template>
                        <template #content>
                            <p class="text-muted fs-14 mb-4 pt-1">
                                Vous êtes sur le point de supprimer le plan <strong>{{ planToDelete?.name }}</strong>.
                            </p>
                        </template>
                    </modal>
                </div>
            </div>
        </div>
    </div>
</template>
